import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";

const BLANK = {
  code: "", name: "", contact_person: "", email: "", phone: "",
  address: "", default_currency: "USD", notes: "", active: true,
};

export default function Customers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const canEdit = can(user.role, "staff");
  const canDelete = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/customers"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setOpen(true); }
  function openEdit(c) { setEditing(c); setForm({ ...BLANK, ...c }); setOpen(true); }
  async function save(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.code && payload.name) {
        payload.code = payload.name.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 12) || "CUST";
      }
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(c) {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="customers-page">
      <PageHeader
        overline="Module / Master Data"
        title="Customers"
        subtitle="Reusable customer list. Used as dropdowns in quotations, orders and delivery notes."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `customers-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="customer-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Customer
              </BtnPrimary>
            )}
          </>
        }
      />
      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No customers yet." /></div> : (
          <table className="phizo-table">
            <thead>
              <tr><th>Code</th><th>Name</th><th>Contact</th><th>Phone</th><th>Email</th><th>Currency</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.code}`}>
                  <td className="mono font-semibold">{c.code}</td>
                  <td className="font-medium">{c.name}</td>
                  <td className="text-sm">{c.contact_person || "—"}</td>
                  <td className="text-sm mono">{c.phone || "—"}</td>
                  <td className="text-sm mono text-[#52525b]">{c.email || "—"}</td>
                  <td className="text-sm mono">{c.default_currency}</td>
                  <td>
                    <span className="phizo-status" style={{ background: c.active ? "#00C853" : "#a1a1aa", color: c.active ? "#09090b" : "#fff", borderColor: c.active ? "#00C853" : "#a1a1aa" }}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {canEdit && <BtnGhost onClick={() => openEdit(c)}><PencilSimple size={12} weight="bold" /></BtnGhost>}
                    {canDelete && <BtnGhost onClick={() => remove(c)} className="text-[#FF2A2A]"><Trash size={12} weight="bold" /></BtnGhost>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Customer" : "Add Customer"} wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Code" hint="Auto-generated from name if left blank">
            <Input disabled={!!editing} data-testid="customer-code"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Name">
            <Input required data-testid="customer-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Contact Person">
            <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Default Currency">
            <Select value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })}>
              <option value="USD">USD</option>
              <option value="ZMW">ZMW</option>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Address"><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <div className="col-span-2">
            <Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active (show in dropdowns)
          </label>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="customer-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
